import { ChevronDown, Crown, MessageSquareText, Trophy } from 'lucide-react';
import type { CSSProperties } from 'react';
import { cn } from '@/lib/utils';
import type { JudgeBallotProject, JudgingCriterion } from '@/types';
import { CriterionName } from './CriterionName';
import { ProjectLogo } from './ProjectLogo';
import { NoteField, SaveIndicator } from './ProjectRow';
import { trackOf } from './scoring';
import { useProjectGrades } from './useProjectGrades';

// The final check's table: place, project, one narrow column per criterion, the
// weighted grade and the note toggle. The header and every row share it, so the
// grades line up down the page for comparing projects. On a phone the criteria wrap
// onto a line of their own under the name.
function columns(criteria: JudgingCriterion[]): CSSProperties {
  return {
    '--final-cols': `2.5rem minmax(0,1fr) repeat(${criteria.length}, 4.5rem) 5rem 2rem`,
  } as CSSProperties;
}

const GRID =
  'grid grid-cols-[2rem_minmax(0,1fr)_auto_auto] items-center gap-x-3 gap-y-2 sm:grid-cols-[var(--final-cols)]';

// A short label for a criterion on a phone, where there is no header row.
function abbreviation(name: string): string {
  return name.slice(0, 3);
}

export function FinalTableHeader({ criteria }: { criteria: JudgingCriterion[] }) {
  return (
    <div
      style={columns(criteria)}
      className={cn(GRID, 'hidden px-3 text-primary/60 text-xs uppercase sm:grid')}
    >
      <span>#</span>
      <span>Proyecto</span>
      {criteria.map((criterion) => (
        <span
          key={criterion.id}
          title={
            criterion.description
              ? undefined
              : `${criterion.name} (${criterion.weight}%)`
          }
          className="flex flex-col items-center text-center leading-tight"
        >
          <CriterionName criterion={criterion} className="max-w-full">
            <span className="min-w-0 truncate">{criterion.name}</span>
          </CriterionName>
          <span className="text-primary/40 normal-case">{criterion.weight}%</span>
        </span>
      ))}
      <span className="text-right">Nota</span>
      <span />
    </div>
  );
}

export function FinalRow({
  stageSlug,
  project,
  criteria,
  rank,
  bestScores,
  overallWinner,
  trackWinner,
  highlighted,
  readOnly,
  expanded,
  onToggle,
  onExpand,
}: {
  stageSlug: string;
  project: JudgeBallotProject;
  criteria: JudgingCriterion[];
  rank: number | null;
  // The best saved grade per criterion among the projects shown with this one, to
  // mark who leads each column.
  bestScores: Record<string, number>;
  overallWinner: boolean;
  trackWinner: boolean;
  highlighted: boolean;
  readOnly: boolean;
  // Whether the description and the internal note are showing.
  expanded: boolean;
  onToggle: () => void;
  // Opens the detail panel.
  onExpand: () => void;
}) {
  const grades = useProjectGrades({ stageSlug, project, criteria });
  const { valueOf, errorOf } = grades;
  const panelId = `final-note-${project.id}`;

  return (
    <div
      className={cn(
        'border-2',
        highlighted ? 'border-primary bg-primary/10' : 'border-primary/30',
        expanded && !highlighted && 'border-primary/60',
      )}
    >
      <div style={columns(criteria)} className={cn(GRID, 'p-3')}>
        <span
          className={cn(
            'font-bold text-lg',
            rank === 1 ? 'text-primary' : 'text-primary/50',
          )}
        >
          {rank === null ? '–' : `${rank}º`}
        </span>

        <button
          type="button"
          onClick={onExpand}
          aria-label={`Ver detalles de ${project.name}`}
          className="group flex min-w-0 items-center gap-2 text-left"
        >
          <ProjectLogo project={project} className="h-8 w-8" />
          <span className="flex min-w-0 flex-col">
            <span className="flex min-w-0 items-center gap-1.5">
              <span className="min-w-0 break-words font-bold text-primary group-hover:underline sm:truncate">
                {project.name}
              </span>
              {overallWinner && (
                <Crown
                  className="h-4 w-4 shrink-0 text-primary"
                  aria-label="Ganador overall"
                />
              )}
              {trackWinner && (
                <Trophy
                  className="h-4 w-4 shrink-0 text-primary"
                  aria-label={`Ganador ${trackOf(project)}`}
                />
              )}
            </span>
            <span className="truncate text-primary/60 text-xs">
              {trackOf(project)} · {project.teamSlug} · orden {project.order}
            </span>
          </span>
        </button>

        {/* A line of its own on a phone; on wider screens each grade is a column. */}
        <span
          className="order-last col-span-full grid gap-2 sm:contents"
          style={{ gridTemplateColumns: `repeat(${criteria.length}, minmax(0, 1fr))` }}
        >
          {criteria.map((criterion) => {
            const saved = project.scores[criterion.id];
            const best = saved !== undefined && saved === bestScores[criterion.id];
            const error = errorOf(criterion.id);
            return (
              <label
                key={criterion.id}
                className="relative flex flex-col items-center gap-0.5"
              >
                <span className="text-[10px] text-primary/50 uppercase sm:hidden">
                  {abbreviation(criterion.name)}
                </span>
                <input
                  inputMode="decimal"
                  placeholder="–"
                  aria-label={`${criterion.name} de ${project.name}`}
                  value={valueOf(criterion.id)}
                  onChange={(e) => grades.setValue(criterion.id, e.target.value)}
                  onBlur={grades.saveNow}
                  readOnly={readOnly}
                  aria-invalid={error ? true : undefined}
                  title={error ?? undefined}
                  className={cn(
                    'h-8 w-full border border-transparent bg-transparent text-center text-sm tabular-nums outline-none transition-colors placeholder:text-primary/30',
                    best ? 'bg-primary/20 font-bold text-primary' : 'text-primary/80',
                    !readOnly &&
                      'border-primary/20 hover:border-primary/50 focus:border-primary focus:bg-background',
                    error && 'border-destructive text-destructive focus:border-destructive',
                  )}
                />
                {/* Floats under the field instead of taking a line, so the row
                    never grows when it shows up. */}
                {error && (
                  <span className="pointer-events-none absolute top-full left-0 z-10 sm:right-0 sm:left-auto mt-0.5 whitespace-nowrap bg-destructive px-1 text-[10px] text-white leading-4">
                    {error}
                  </span>
                )}
              </label>
            );
          })}
        </span>

        <span className="flex flex-col items-end gap-1">
          <span className="flex items-center gap-1">
            {!readOnly && <SaveIndicator status={grades.status} iconOnly />}
            <span className="bg-primary px-2 py-0.5 font-bold text-background tabular-nums">
              {grades.weighted === null ? '–' : grades.weighted.toFixed(2)}
            </span>
          </span>
          <span className="h-1 w-14 bg-primary/15" aria-hidden>
            <span
              className="block h-full bg-primary"
              style={{ width: `${((grades.weighted ?? 0) / 5) * 100}%` }}
            />
          </span>
        </span>

        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          aria-controls={panelId}
          aria-label={`${expanded ? 'Ocultar' : 'Ver'} descripción y nota interna de ${project.name}`}
          title="Descripción y nota interna"
          className="relative flex h-8 w-8 items-center justify-center justify-self-end text-primary hover:bg-primary/10"
        >
          <ChevronDown
            className={cn('h-5 w-5 transition-transform', expanded && 'rotate-180')}
          />
          {project.note && !expanded && (
            <MessageSquareText
              className="absolute -top-1 -right-1 h-3.5 w-3.5 text-primary/70"
              aria-hidden
            />
          )}
        </button>
      </div>

      {expanded && (
        <div
          id={panelId}
          className="flex flex-col gap-3 border-primary/30 border-t px-3 pt-3 pb-4"
        >
          {(project.description || project.oneliner) && (
            <div className="flex flex-col gap-1">
              <span className="text-primary/80 text-xs">Descripción</span>
              <p className="whitespace-pre-line text-primary/90 text-sm leading-relaxed">
                {project.description || project.oneliner}
              </p>
            </div>
          )}
          {readOnly ? (
            <div className="flex flex-col gap-1">
              <span className="text-primary/80 text-xs">Nota interna</span>
              <p className="whitespace-pre-line border-primary/30 border-l-2 pl-3 text-primary/90 text-sm">
                {project.note || 'Sin nota interna.'}
              </p>
            </div>
          ) : (
            <NoteField grades={grades} />
          )}
          {grades.errors.base && (
            <p className="text-destructive text-sm">{grades.errors.base}</p>
          )}
        </div>
      )}
    </div>
  );
}
